import React, { useEffect, useState } from "react";
import axios from "axios";

import {
  Card,
  Row,
  CardBody,
  Col,
  FormGroup,
  Label,
  Button,
  Alert,
  Input,
  Tooltip
} from "reactstrap";
import { useFetch } from "../../utils/fetchHook";
import { getCookie } from "../../jwt/_helpers/cookie";
import TableHelper from "../../components/tableHelper";
import { Redirect } from "react-router-dom";
import Select from "react-select";
import { useTranslation } from "react-i18next";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;

const SongParametersUpdate = ({ match, name }) => {
  const token = getCookie("token");
  const currentID = match.url.split("/")[2];
  const { t } = useTranslation();
  const [songData, error, loading] = useFetch(
    "GET",
    `audio-mastering-songs/${currentID}/`,
    token
  );
  const [success, setSuccess] = useState(false);
  const [errorPut, setErrorPut] = useState(false);
  const [redirect, setRedirect] = useState(false);
  const [masteringID, setMasteringID] = useState(null);

  const [song_intensity, setSong_intensity] = useState(null);
  const [song_loudness, setSong_loudness] = useState(null);
  const [song_preset, setSong_preset] = useState(null);
  const [song_mastered, setSong_mastered] = useState(false);

  const [intensityTooltipOpen, setIntensityTooltipOpen] = useState(false);
  const [loudnessTooltipOpen, setLoudnessTooltipOpen] = useState(false);
  
  const toggleIntensityTooltip = () => setIntensityTooltipOpen(!intensityTooltipOpen);
  const toggleLoudnessTooltip = () => setLoudnessTooltipOpen(!loudnessTooltipOpen);

  const presetOps = [
    { value: 'm', label: t("mastering.songParametersUpdate.option.preset.acoustic") },
    { value: 'i', label: t("mastering.songParametersUpdate.option.preset.allGenres") },
    { value: 'n', label: t("mastering.songParametersUpdate.option.preset.classical") },
    { value: 'b', label: t("mastering.songParametersUpdate.option.preset.clubHouseTechno") },
    { value: 'h', label: t("mastering.songParametersUpdate.option.preset.edmMusic") },
    { value: 'f', label: t("mastering.songParametersUpdate.option.preset.electronic") },
    { value: 'g', label: t("mastering.songParametersUpdate.option.preset.electronicRaw") },
    { value: 'c', label: t("mastering.songParametersUpdate.option.preset.hipHop") },
    { value: 'd', label: t("mastering.songParametersUpdate.option.preset.hipHopBass") },
    { value: 'a', label: t("mastering.songParametersUpdate.option.preset.popMusic") },
    { value: 'k', label: t("mastering.songParametersUpdate.option.preset.popRnB") },
    { value: 'j', label: t("mastering.songParametersUpdate.option.preset.rockMetal") },
    { value: 'e', label: t("mastering.songParametersUpdate.option.preset.trap") },
    { value: 'l', label: t("mastering.songParametersUpdate.option.preset.vocalFocused") }
  ];

  useEffect(() => {
    if (songData.audio_mastering) {
      setMasteringID(songData.audio_mastering);
    }
    if (songData.intensity) {
      setSong_intensity(songData.intensity);
    }
    if (songData.target_level) {
      setSong_loudness(songData.target_level);
    }
    if (songData.preset) {
      const matchingPreset = presetOps.find(preset => preset.value === songData.preset);
      if (matchingPreset) {
          setSong_preset(matchingPreset);
      }
    }
    if (songData.audio_mastering) {
      setSong_mastered(songData.mastering_expedited);
    }
  }, [songData]);

  const options = {
    method: "PATCH",
    mode: 'cors',
    headers: {
      Authorization: `Bearer ${token}`,
      "x-api-key": X_API_KEY,
      "Content-Type": "application/json"
    }
  };

  const updateParameters = () => {

    const updatedData = {
      ...(song_intensity && (songData.intensity !== song_intensity) && {"intensity": song_intensity}),
      ...(song_preset && (songData.preset !== song_preset.value) && {"preset": song_preset.value}),
      ...(song_loudness && (songData.target_level !== song_loudness) && {"target_level": song_loudness}),
    };

    if (Object.keys(updatedData).length === 0) {
      return false;
    }

    axios
      .patch(
        `${API_URL}audio-mastering-songs/${currentID}/`,
        updatedData,
        options
      )
      .then(res => {
        setSuccess(true);
        setTimeout(() => setRedirect(true), 1000);
      })
      .catch(err => {
        setErrorPut(true);
      });
  }

  return (
    <div>
      {loading ? (
        <TableHelper loading />
      ) : error ? (
        <TableHelper error />
      ) : (
        <>
        {
          song_mastered ? (
            <Alert color="danger">
              {t("mastering.songParametersUpdate.message.songMastered")}
            </Alert>
          ) : (
            <Card>
            <CardBody>
              <Row>
                <Col sm={4}>
                  <h3>{t("mastering.songParametersUpdate.title")}</h3>
                  <FormGroup row>
                    <Label for="intensity" sm={4}>
                      {t("mastering.songParametersUpdate.form.label.intensity")}
                      <i className="ml-1 tooltip-icon mdi mdi-18px mdi-help-circle" id="intensityTooltipIcon"></i>
                      <Tooltip placement="top" isOpen={intensityTooltipOpen} target="intensityTooltipIcon" toggle={toggleIntensityTooltip}>
                        {t("mastering.songParametersUpdate.message.intensityTooltip")}
                      </Tooltip>
                    </Label>
                    <Col>
                      <Input
                        name="intensity"
                        type="range"
                        defaultValue={songData.intensity}
                        min="1"
                        max="100"
                        step="1"
                        className="mt-2"
                        onChange={e => {
                          setSong_intensity(e.target.value);
                        }}
                      />
                      <output id="intensity-val"><b>{song_intensity} %</b></output>
                    </Col>
                  </FormGroup>

                  <FormGroup row>
                    <Label for="loudness" sm={4}>
                      {t("mastering.songParametersUpdate.form.label.loudness")}
                      <i className="ml-1 tooltip-icon mdi mdi-18px mdi-help-circle" id="loudnessTooltipIcon"></i>
                      <Tooltip placement="top" isOpen={loudnessTooltipOpen} target="loudnessTooltipIcon" toggle={toggleLoudnessTooltip}>
                        {t("mastering.songParametersUpdate.message.loudnessTooltip")}
                      </Tooltip>
                    </Label>
                    <Col>
                      <Input
                        name="loudness"
                        type="range"
                        defaultValue={songData.target_level}
                        min="-22"
                        max="-6"
                        step="1"
                        className="mt-2"
                        onChange={e => {
                          setSong_loudness(e.target.value);
                        }}
                      />
                      <output id="loudness-val"><b>{song_loudness} dB</b></output>
                    </Col>
                  </FormGroup>
                  
                  <FormGroup row>
                    <Label for="preset" sm={4}>
                      {t("mastering.songParametersUpdate.form.label.preset")}
                    </Label>
                    <Col>
                      <Select
                        name="preset"
                        closeMenuOnSelect={true}
                        options={presetOps}
                        value={song_preset}
                        onChange={(e) => {
                          setSong_preset(e);
                        }}
                        placeholder={t("mastering.songParametersUpdate.placeholder.preset")}
                        className="artistType"
                      />
                    </Col>
                  </FormGroup>

                  <FormGroup row>
                    <Col>
                      <Button color="success" onClick={updateParameters}>
                        {t("mastering.songParametersUpdate.form.button.submit")}
                      </Button>
                    </Col>
                  </FormGroup>

                  {success && (
                    <Alert color="success">
                      {t("mastering.songParametersUpdate.message.success")}
                    </Alert>
                  )}
                  {redirect ? <Redirect to={`/mastering/${masteringID}/view`} /> : null}
                  {errorPut && (
                    <Alert color="danger">
                      {t("mastering.songParametersUpdate.message.error")}
                    </Alert>
                  )}
                </Col>
              </Row>
              </CardBody>
            </Card>
          )
        }
        </>
      )}
    </div>
  );
};

export default SongParametersUpdate;
